import React, { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { STATUS_FLAGS, getActiveStatuses } from '../utils/statusHelpers'

function HitListDetail() {
  const { id } = useParams()
  const { user } = useAuth()
  const [hitlist, setHitlist] = useState(null)
  const [items, setItems] = useState([])
  const [ownerUsername, setOwnerUsername] = useState('')
  const [loading, setLoading] = useState(true)

  // Collection statuses: variant_id -> boolean status object
  const [collectionMap, setCollectionMap] = useState({})

  // Filter options
  const [brands, setBrands] = useState([])
  const [lureTypes, setLureTypes] = useState([])

  // Search / add state
  const [filterBrand, setFilterBrand] = useState('')
  const [filterType, setFilterType] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [selectedBaseLure, setSelectedBaseLure] = useState(null)
  const [availableVariants, setAvailableVariants] = useState([])
  const [selectedVariant, setSelectedVariant] = useState(null)
  const [itemNote, setItemNote] = useState('')
  const [addingItem, setAddingItem] = useState(false)
  const [addError, setAddError] = useState('')
  const [addSuccess, setAddSuccess] = useState('')

  // Sort state
  const [sortMode, setSortMode] = useState('custom')

  // Drag and drop state (custom sort, owner only)
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)

  useEffect(() => {
    fetchHitlist()
    fetchFilterOptions()
  }, [id])

  useEffect(() => {
    if (user && items.length > 0) fetchCollectionStatuses()
  }, [user?.id, items.length])

  // Trigger search whenever any filter changes
  useEffect(() => {
    const hasFilter = filterBrand || filterType || searchQuery.trim().length >= 2
    if (!hasFilter) { setSearchResults([]); return }
    const timer = setTimeout(runLureSearch, 300)
    return () => clearTimeout(timer)
  }, [filterBrand, filterType, searchQuery])

  const fetchHitlist = async () => {
    try {
      const { data: hitlistData, error: hitlistError } = await supabase
        .from('hitlists')
        .select('*')
        .eq('hitlist_id', id)
        .single()

      if (hitlistError) throw hitlistError
      setHitlist(hitlistData)

      const { data: ownerData } = await supabase
        .from('users')
        .select('username')
        .eq('id', hitlistData.user_id)
        .single()
      setOwnerUsername(ownerData?.username || 'Unknown')

      const { data: itemsData, error: itemsError } = await supabase
        .from('hitlist_items')
        .select(`
          *,
          lure_variants (
            variant_id,
            color_name,
            variant_name,
            image_url,
            base_lures (
              base_lure_id,
              name,
              weight,
              length,
              brands (brand_name),
              lure_types (type_name),
              lure_reviews (rating)
            )
          )
        `)
        .eq('hitlist_id', id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })

      if (itemsError) throw itemsError

      const addedByIds = [...new Set((itemsData || []).map(i => i.added_by))]
      const { data: addedByUsers } = addedByIds.length > 0
        ? await supabase.from('users').select('id, username').in('id', addedByIds)
        : { data: [] }

      const userMap = {}
      addedByUsers?.forEach(u => { userMap[u.id] = u.username })

      setItems((itemsData || []).map(item => ({
        ...item,
        addedByUsername: userMap[item.added_by] || 'Unknown'
      })))

      setLoading(false)
    } catch (err) {
      console.error('Error fetching hitlist:', err)
      setLoading(false)
    }
  }

  const fetchFilterOptions = async () => {
    const [{ data: brandsData }, { data: typesData }] = await Promise.all([
      supabase.from('brands').select('brand_id, brand_name').order('brand_name'),
      supabase.from('lure_types').select('type_id, type_name').order('type_name')
    ])
    setBrands(brandsData || [])
    setLureTypes(typesData || [])
  }

  const fetchCollectionStatuses = async () => {
    if (!user || items.length === 0) return
    try {
      const variantIds = items.map(i => i.variant_id)
      const cols = STATUS_FLAGS.map(s => s.key).join(', ')
      const { data: inventory } = await supabase
        .from('user_lure_inventory')
        .select(`variant_id, ${cols}`)
        .eq('user_id', user.id)
        .in('variant_id', variantIds)

      const map = {}
      inventory?.forEach(inv => { map[inv.variant_id] = inv })
      setCollectionMap(map)
    } catch (err) {
      console.error('Error fetching collection statuses:', err)
    }
  }

  const runLureSearch = async () => {
    setSelectedBaseLure(null)
    setSelectedVariant(null)
    setAvailableVariants([])
    setSearchLoading(true)
    try {
      let query = supabase
        .from('base_lures')
        .select('base_lure_id, name, brands (brand_id, brand_name), lure_types (type_id, type_name)')
        .order('name')
        .limit(25)

      if (filterBrand) query = query.eq('brand_id', parseInt(filterBrand))
      if (filterType) query = query.eq('type_id', parseInt(filterType))
      if (searchQuery.trim()) query = query.ilike('name', `%${searchQuery.trim()}%`)

      const { data } = await query
      setSearchResults(data || [])
    } catch (err) {
      console.error('Search error:', err)
    } finally {
      setSearchLoading(false)
    }
  }

  const handleSelectBaseLure = async (lure) => {
    setSelectedBaseLure(lure)
    setSelectedVariant(null)
    setAvailableVariants([])
    setAddError('')
    const { data } = await supabase
      .from('lure_variants')
      .select('variant_id, color_name, variant_name, image_url')
      .eq('base_lure_id', lure.base_lure_id)
      .order('color_name')
    setAvailableVariants(data || [])
  }

  const handleAddLure = async () => {
    if (!selectedVariant || !user) return
    setAddingItem(true)
    setAddError('')
    setAddSuccess('')
    try {
      const { error } = await supabase
        .from('hitlist_items')
        .insert([{
          hitlist_id: parseInt(id),
          variant_id: selectedVariant.variant_id,
          added_by: user.id,
          notes: itemNote.trim() || null,
          sort_order: items.length
        }])

      if (error) {
        if (error.code === '23505') {
          setAddError('That color variant is already on this list.')
        } else {
          throw error
        }
        return
      }

      const colorLabel = selectedVariant.variant_name || selectedVariant.color_name
      setAddSuccess(`"${selectedBaseLure.name} — ${colorLabel}" added`)
      setSelectedBaseLure(null)
      setSelectedVariant(null)
      setAvailableVariants([])
      setItemNote('')
      setAddError('')
      await fetchHitlist()
    } catch (err) {
      setAddError(err.message || 'Failed to add lure.')
    } finally {
      setAddingItem(false)
    }
  }

  const handleRemoveItem = async (itemId, label) => {
    if (!window.confirm(`Remove "${label}" from this list?`)) return
    try {
      const { error } = await supabase.from('hitlist_items').delete().eq('item_id', itemId)
      if (error) throw error
      await fetchHitlist()
    } catch (err) {
      console.error(err)
      alert('Failed to remove item.')
    }
  }

  // ── Sorting ──────────────────────────────────────────────────────

  const getAvgRating = (item) => {
    const reviews = item.lure_variants?.base_lures?.lure_reviews
    if (!reviews || reviews.length === 0) return null
    const sum = reviews.reduce((acc, r) => acc + (r.rating || 0), 0)
    return sum / reviews.length
  }

  const displayedItems = useMemo(() => {
    if (sortMode === 'custom') return items
    const sorted = [...items]
    if (sortMode === 'brand') {
      sorted.sort((a, b) =>
        (a.lure_variants?.base_lures?.brands?.brand_name || '').localeCompare(
          b.lure_variants?.base_lures?.brands?.brand_name || ''
        )
      )
    } else if (sortMode === 'category') {
      sorted.sort((a, b) =>
        (a.lure_variants?.base_lures?.lure_types?.type_name || '').localeCompare(
          b.lure_variants?.base_lures?.lure_types?.type_name || ''
        )
      )
    } else if (sortMode === 'color') {
      sorted.sort((a, b) => {
        const aColor = a.lure_variants?.variant_name || a.lure_variants?.color_name || ''
        const bColor = b.lure_variants?.variant_name || b.lure_variants?.color_name || ''
        return aColor.localeCompare(bColor)
      })
    } else if (sortMode === 'rating') {
      sorted.sort((a, b) => {
        const aRating = getAvgRating(a)
        const bRating = getAvgRating(b)
        if (aRating === null && bRating === null) return 0
        if (aRating === null) return 1
        if (bRating === null) return -1
        return bRating - aRating
      })
    }
    return sorted
  }, [items, sortMode])

  // ── Drag and Drop (custom mode, owner only) ───────────────────────

  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (index !== dragOverIndex) setDragOverIndex(index)
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null)
      setDragOverIndex(null)
      return
    }

    const reordered = [...items]
    const [moved] = reordered.splice(draggedIndex, 1)
    reordered.splice(dropIndex, 0, moved)

    // Optimistic update — persist in background
    setItems(reordered)
    setDraggedIndex(null)
    setDragOverIndex(null)
    saveCustomOrder(reordered)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const saveCustomOrder = async (orderedItems) => {
    try {
      await Promise.all(
        orderedItems.map((item, index) =>
          supabase
            .from('hitlist_items')
            .update({ sort_order: index })
            .eq('item_id', item.item_id)
        )
      )
    } catch (err) {
      console.error('Error saving custom order:', err)
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────

  const getCollectionBadge = (variantId) => {
    const inv = collectionMap[variantId]
    if (!inv) return null
    const active = getActiveStatuses(inv)
    if (active.length === 0) return null
    return (
      <span className="collection-status-badge-wrap">
        {active.map(s => (
          <span key={s.key} className={`collection-status-badge ${s.cssClass}`}>{s.label}</span>
        ))}
      </span>
    )
  }

  const isOwner = user && hitlist && user.id === hitlist.user_id
  const canAddItems = user && hitlist && (isOwner || hitlist.is_open_contributions)
  const addedVariantIds = new Set(items.map(i => i.variant_id))
  const isDragMode = sortMode === 'custom' && isOwner

  if (loading) return <div className="loading">Loading HitList...</div>

  if (!hitlist) {
    return (
      <div className="error-page">
        <h2>HitList Not Found</h2>
        <p>This list doesn't exist or is private.</p>
        <Link to="/hitlists" className="btn-primary">← Browse HitLists</Link>
      </div>
    )
  }

  return (
    <div className="hitlist-detail-page">
      <div className="breadcrumb">
        <Link to="/hitlists">HitLists</Link> → <span>{hitlist.title}</span>
      </div>

      {/* Header */}
      <div className="hitlist-detail-header">
        <div className="hitlist-detail-title-row">
          <h1>{hitlist.title}</h1>
          <div className="hitlist-badges">
            <span className={`hitlist-privacy-badge ${hitlist.is_public ? 'public' : 'private'}`}>
              {hitlist.is_public ? '🌐 Public' : '🔒 Private'}
            </span>
            {hitlist.is_open_contributions && (
              <span className="hitlist-privacy-badge open">👥 Open Contributions</span>
            )}
          </div>
        </div>

        {hitlist.description && (
          <p className="hitlist-detail-description">{hitlist.description}</p>
        )}

        <div className="hitlist-detail-meta">
          <span>Created by <Link to={`/profile/${hitlist.user_id}`}>{ownerUsername}</Link></span>
          <span>{new Date(hitlist.created_at).toLocaleDateString()}</span>
          <span>{items.length} {items.length === 1 ? 'entry' : 'entries'}</span>
        </div>

        {isOwner && (
          <Link to="/my-hitlists" className="btn-secondary" style={{ marginTop: '0.75rem', display: 'inline-block' }}>
            ⚙️ Manage List Settings
          </Link>
        )}
      </div>

      {/* Add Lure Section */}
      {canAddItems && (
        <div className="hitlist-add-section">
          <h3>Add a Lure</h3>

          {addError && <div className="alert-error" style={{ marginBottom: '0.75rem' }}>{addError}</div>}
          {addSuccess && (
            <div className="contextual-banner contextual-banner-success" style={{ marginBottom: '0.75rem' }}>
              <p>✅ {addSuccess}</p>
            </div>
          )}

          <div className="hitlist-search-filters">
            <select value={filterBrand} onChange={e => setFilterBrand(e.target.value)} className="filter-select">
              <option value="">All Brands</option>
              {brands.map(b => (
                <option key={b.brand_id} value={b.brand_id}>{b.brand_name}</option>
              ))}
            </select>

            <select value={filterType} onChange={e => setFilterType(e.target.value)} className="filter-select">
              <option value="">All Categories</option>
              {lureTypes.map(t => (
                <option key={t.type_id} value={t.type_id}>{t.type_name}</option>
              ))}
            </select>

            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="filter-input"
              placeholder="Search by name..."
            />
          </div>

          {searchLoading && <p className="search-hint">Searching...</p>}

          {!searchLoading && searchResults.length === 0 && (filterBrand || filterType || searchQuery.length >= 2) && (
            <p className="search-hint">No lures found. Try adjusting your filters.</p>
          )}

          {!filterBrand && !filterType && searchQuery.length < 2 && (
            <p className="search-hint">Select a brand or category, or type at least 2 characters to search.</p>
          )}

          {searchResults.length > 0 && (
            <div className="lure-search-results-list">
              {searchResults.map(lure => (
                <div key={lure.base_lure_id}>
                  <button
                    className={`lure-search-result-item ${selectedBaseLure?.base_lure_id === lure.base_lure_id ? 'selected' : ''}`}
                    onClick={() => handleSelectBaseLure(lure)}
                  >
                    <strong>{lure.name}</strong>
                    <span className="result-meta"> — {lure.brands?.brand_name} · {lure.lure_types?.type_name}</span>
                    <span className="result-arrow">{selectedBaseLure?.base_lure_id === lure.base_lure_id ? '▼' : '▶'}</span>
                  </button>

                  {selectedBaseLure?.base_lure_id === lure.base_lure_id && (
                    <div className="variant-picker-panel">
                      {availableVariants.length === 0 ? (
                        <p className="search-hint">No color variants found for this lure.</p>
                      ) : (
                        <>
                          <p className="variant-picker-label">Select a color:</p>
                          <div className="variant-chip-grid">
                            {availableVariants.map(variant => {
                              const alreadyAdded = addedVariantIds.has(variant.variant_id)
                              const isSelected = selectedVariant?.variant_id === variant.variant_id
                              return (
                                <button
                                  key={variant.variant_id}
                                  className={`variant-chip ${isSelected ? 'active' : ''} ${alreadyAdded ? 'already-added' : ''}`}
                                  onClick={() => !alreadyAdded && setSelectedVariant(isSelected ? null : variant)}
                                  disabled={alreadyAdded}
                                  title={alreadyAdded ? 'Already on this list' : ''}
                                >
                                  {variant.variant_name || variant.color_name}
                                  {alreadyAdded && ' ✓'}
                                </button>
                              )
                            })}
                          </div>

                          {selectedVariant && (
                            <div className="hitlist-add-confirm">
                              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                                <label>Note <span style={{ fontWeight: 'normal', color: '#64748b' }}>(Optional)</span></label>
                                <textarea
                                  value={itemNote}
                                  onChange={e => setItemNote(e.target.value)}
                                  className="form-textarea"
                                  rows="2"
                                  placeholder='e.g. "Deadly on cloudy days in shallow water"'
                                />
                              </div>
                              <div className="form-actions">
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => { setSelectedVariant(null); setItemNote('') }}
                                >
                                  Clear
                                </button>
                                <button
                                  type="button"
                                  className="btn-primary"
                                  onClick={handleAddLure}
                                  disabled={addingItem}
                                >
                                  {addingItem ? 'Adding...' : 'Add to HitList'}
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!canAddItems && user && !isOwner && !hitlist.is_open_contributions && (
        <p className="hitlist-closed-note">This list only accepts contributions from its owner.</p>
      )}

      {!user && (
        <p className="hitlist-closed-note">
          <Link to="/login">Log in</Link> to add lures to this list.
        </p>
      )}

      {/* Sort Bar */}
      {items.length > 0 && (
        <div className="hitlist-sort-bar">
          <span className="sort-label">Sort:</span>
          {[
            { key: 'custom', label: '⠿ Custom' },
            { key: 'brand',    label: 'Brand' },
            { key: 'category', label: 'Category' },
            { key: 'color',    label: 'Color' },
            { key: 'rating',   label: '★ Rating' },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`sort-btn${sortMode === key ? ' active' : ''}`}
              onClick={() => setSortMode(key)}
            >
              {label}
            </button>
          ))}
          {sortMode === 'custom' && isOwner && (
            <span className="sort-hint">Drag to reorder · saves automatically</span>
          )}
          {sortMode === 'rating' && (
            <span className="sort-hint">Highest average site rating first · unrated lures last</span>
          )}
        </div>
      )}

      {/* Lure List */}
      {items.length === 0 ? (
        <div className="no-content-box" style={{ marginTop: '1.5rem' }}>
          <p>No lures on this list yet.{canAddItems ? ' Use the search above to add the first one!' : ''}</p>
        </div>
      ) : (
        <div className="hitlist-items">
          {displayedItems.map((item, index) => {
            const variant = item.lure_variants
            const baseLure = variant?.base_lures
            const colorLabel = variant?.variant_name || variant?.color_name
            const itemLabel = `${baseLure?.name} — ${colorLabel}`
            const avgRating = getAvgRating(item)

            const isDraggingThis = isDragMode && draggedIndex === index
            const isDragOverThis = isDragMode && dragOverIndex === index

            return (
              <div
                key={item.item_id}
                className={[
                  'hitlist-item-card',
                  isDragMode ? 'drag-enabled' : '',
                  isDraggingThis ? 'dragging' : '',
                  isDragOverThis ? 'drag-over' : '',
                ].filter(Boolean).join(' ')}
                draggable={isDragMode}
                onDragStart={isDragMode ? (e) => handleDragStart(e, index) : undefined}
                onDragOver={isDragMode ? (e) => handleDragOver(e, index) : undefined}
                onDrop={isDragMode ? (e) => handleDrop(e, index) : undefined}
                onDragEnd={isDragMode ? handleDragEnd : undefined}
              >
                {/* Drag handle — only shown to owner in custom mode */}
                {isDragMode && (
                  <div className="drag-handle" title="Drag to reorder">⠿</div>
                )}

                <div className="hitlist-item-number">{index + 1}</div>

                {variant?.image_url && (
                  <div className="hitlist-item-image">
                    <img src={variant.image_url} alt={colorLabel} />
                  </div>
                )}

                <div className="hitlist-item-body">
                  <div className="hitlist-item-name-row">
                    <Link to={`/lure/${baseLure?.base_lure_id}`} className="hitlist-item-name">
                      {baseLure?.name}
                    </Link>
                    <span className="hitlist-item-color">— {colorLabel}</span>
                    {user && getCollectionBadge(item.variant_id)}
                  </div>

                  <div className="hitlist-item-meta">
                    <span>{baseLure?.brands?.brand_name}</span>
                    <span>{baseLure?.lure_types?.type_name}</span>
                    {baseLure?.weight && <span>{baseLure.weight}</span>}
                    {baseLure?.length && <span>{baseLure.length}</span>}
                    {avgRating !== null && (
                      <span className="item-avg-rating">
                        ★ {avgRating.toFixed(1)}
                        <span className="item-rating-count">
                          ({baseLure.lure_reviews.length} {baseLure.lure_reviews.length === 1 ? 'review' : 'reviews'})
                        </span>
                      </span>
                    )}
                  </div>

                  {item.notes && (
                    <p className="hitlist-item-note">"{item.notes}"</p>
                  )}

                  {hitlist.is_open_contributions && item.added_by !== hitlist.user_id && (
                    <p className="hitlist-item-added-by">Added by {item.addedByUsername}</p>
                  )}
                </div>

                {(isOwner || (user && item.added_by === user.id)) && (
                  <button
                    className="hitlist-item-remove"
                    onClick={() => handleRemoveItem(item.item_id, itemLabel)}
                    title="Remove from list"
                  >
                    ✕
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default HitListDetail
